// src/components/Widgets/PriceTicker.js
import React, { useEffect, useRef, useState } from "react";
import { useStream } from "../../context/StreamContext";
import CBExchange from "../../utils/datafeed/CBExchangeClass";

const PriceTicker = ({ symbol = "BTC/USDC", exchangeId = "BybitSpot" }) => {
  const stream = useStream();
  const [price, setPrice] = useState(null);
  // Tracks whether a live WS candle has arrived; the REST seed must never
  // overwrite a fresher streamed value.
  const hasLivePriceRef = useRef(false);

  // Seed the price once via REST so the ticker shows a value immediately
  // instead of "Loading..." until the first candleUpdate push arrives.
  // Uses the OHLCV endpoint (same one the chart datafeed uses) and takes the
  // last candle's close — the ticker route does not exist on the backend.
  useEffect(() => {
    let cancelled = false;
    hasLivePriceRef.current = false;
    setPrice(null);

    const now = Date.now();
    CBExchange.fetchOHLCVData(exchangeId, symbol, "1m", now - 10 * 60 * 1000, now, 3)
      .then((resp) => {
        if (cancelled || hasLivePriceRef.current) return;
        if (resp?.status !== "ok" || !Array.isArray(resp.data) || resp.data.length === 0) return;
        // Candles are [timestamp, open, high, low, close, volume]
        const lastCandle = resp.data[resp.data.length - 1];
        const close = parseFloat(lastCandle?.[4]);
        if (Number.isFinite(close) && close > 0) {
          setPrice(close);
        }
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [symbol, exchangeId]);

  // Live updates via the candle WebSocket stream.
  useEffect(() => {
    if (!stream) return undefined;

    const handleCandle = (data) => {
      const close = data?.data?.close;
      if (close != null) {
        hasLivePriceRef.current = true;
        setPrice(close);
      }
    };

    stream.subscribeCandles(exchangeId, symbol, handleCandle);
    return () => {
      stream.unsubscribeCandle(exchangeId, symbol, handleCandle);
    };
  }, [stream, symbol, exchangeId]);

  return (
    <div style={{
      background: "#222",
      color: "#fff",
      padding: "6px 12px",
      borderRadius: "8px",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      minWidth: "80px"
    }}>
      <div style={{
        fontWeight: "bold",
        fontSize: "0.95em",
        marginBottom: "4px",
        textAlign: "center"
      }}>
        {symbol}
      </div>
      <div style={{
        fontSize: "1em",
        textAlign: "center"
      }}>
        {price ? Number(price).toLocaleString("en-US", { minimumFractionDigits: 2 }) : "Loading..."}
      </div>
    </div>
  );
};

export default PriceTicker;
